import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons (Leaflet + webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom colored marker factory
const createColoredIcon = (color = '#c9762b') => {
  return L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="
      background-color: ${color};
      width: 28px;
      height: 28px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    "><div style="
      width: 10px;
      height: 10px;
      background: white;
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    "></div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Auto-fit bounds to all markers
function FitBounds({ positions }) {
  const map = useMap();
  React.useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [map, positions]);
  return null;
}

/**
 * Reusable Map component for the AADC platform
 * 
 * @param {Array} markers - Array of { id, lat, lng, title, description, color, onClick }
 * @param {string} height - CSS height (default: '400px')
 * @param {Array} center - [lat, lng] center (default: auto-fit)
 * @param {number} zoom - Initial zoom level
 * @param {Function} renderPopup - Custom popup renderer (marker) => JSX
 * @param {string} className - Additional CSS classes
 */
const MapView = ({ 
  markers = [], 
  height = '400px', 
  center,
  zoom = 4,
  renderPopup,
  className = '',
  dataTestId = 'map-view'
}) => {
  // Filter markers with valid coordinates
  const validMarkers = useMemo(
    () => markers.filter(m => m.lat && m.lng && !isNaN(m.lat) && !isNaN(m.lng)),
    [markers]
  );

  // Calculate center from markers if not provided
  const mapCenter = useMemo(() => {
    if (center) return center;
    if (validMarkers.length === 0) return [39.8283, -98.5795]; // US center
    const avgLat = validMarkers.reduce((sum, m) => sum + m.lat, 0) / validMarkers.length;
    const avgLng = validMarkers.reduce((sum, m) => sum + m.lng, 0) / validMarkers.length;
    return [avgLat, avgLng];
  }, [center, validMarkers]);

  if (validMarkers.length === 0) {
    return (
      <div 
        className={`flex items-center justify-center bg-muted/50 rounded-lg border ${className}`}
        style={{ height }}
        data-testid={dataTestId}
      >
        <p className="text-muted-foreground text-sm">No locations to display on map</p>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-lg overflow-hidden border shadow-sm ${className}`} 
      style={{ height }}
      data-testid={dataTestId}
    >
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds positions={validMarkers} />
        {validMarkers.map((marker) => (
          <Marker
            key={marker.id}
            position={[marker.lat, marker.lng]}
            icon={createColoredIcon(marker.color || '#c9762b')}
            eventHandlers={marker.onClick ? { click: marker.onClick } : {}}
          >
            {renderPopup ? (
              <Popup>{renderPopup(marker)}</Popup>
            ) : (
              <Popup>
                <div className="text-sm">
                  <strong>{marker.title}</strong>
                  {marker.description && <p className="text-gray-600 mt-1">{marker.description}</p>}
                </div>
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapView;
